import { SplatContact } from "providers/Types";

const CONTACT_VALIDATION_STATUS = {
  BLOCKED_DUPLICATE_PHONE: "Blocked for Duplicate Phone",
  BLOCKED_DUPLICATE_EMAIL: "Blocked for Duplicate Email",
  BLOCKED_DUPLICATE_EMAIL_AND_PHONE: "Blocked for Duplicate Email and Phone",
} as const;

const isBlockedValidationStatus = (validationStatus?: string): boolean =>
  !!validationStatus?.startsWith("Blocked");

const isDuplicatePhoneValidationStatus = (validationStatus?: string): boolean =>
  validationStatus === CONTACT_VALIDATION_STATUS.BLOCKED_DUPLICATE_PHONE ||
  validationStatus ===
    CONTACT_VALIDATION_STATUS.BLOCKED_DUPLICATE_EMAIL_AND_PHONE;

const isDuplicateEmailValidationStatus = (validationStatus?: string): boolean =>
  validationStatus === CONTACT_VALIDATION_STATUS.BLOCKED_DUPLICATE_EMAIL ||
  validationStatus ===
    CONTACT_VALIDATION_STATUS.BLOCKED_DUPLICATE_EMAIL_AND_PHONE;

const getBlockedSignerContact = (
  contacts: Array<Pick<SplatContact, "validationStatus"> | undefined>
): Pick<SplatContact, "validationStatus"> | undefined =>
  contacts.find((contact) =>
    isBlockedValidationStatus(contact?.validationStatus)
  );

const getBlockedValidationDocSigningMessage = (
  validationStatus?: string
): string | undefined => {
  switch (validationStatus) {
    case CONTACT_VALIDATION_STATUS.BLOCKED_DUPLICATE_EMAIL:
      return "Duplicate email detected. Please update it before signing the Sunrun Agreement.";
    case CONTACT_VALIDATION_STATUS.BLOCKED_DUPLICATE_PHONE:
      return "Duplicate phone number detected. Please update it before signing the Sunrun Agreement.";
    case CONTACT_VALIDATION_STATUS.BLOCKED_DUPLICATE_EMAIL_AND_PHONE:
      return "Duplicate email and phone number detected. Please update them before signing the Sunrun Agreement.";
    default:
      return isBlockedValidationStatus(validationStatus)
        ? "Please resolve the contact validation issue before signing documents."
        : undefined;
  }
};

const isMissingContactValue = (value?: string): boolean =>
  !value?.trim() || value.trim() === "-";

const getMissingContactInfoMessage = (
  phone?: string,
  email?: string
): string | undefined => {
  const isMissingPhone = isMissingContactValue(phone);
  const isMissingEmail = isMissingContactValue(email);

  if (isMissingEmail && isMissingPhone) {
    return "If email and phone number are missing, please update them before signing the Sunrun Agreement.";
  }
  if (isMissingEmail) {
    return "If email is missing, please update it before signing the Sunrun Agreement.";
  }
  if (isMissingPhone) {
    return "If phone number is missing, please update it before signing the Sunrun Agreement.";
  }
  return undefined;
};

const getContactValidationDocSigningLock = (
  signers: Array<Pick<SplatContact, "validationStatus"> | undefined>,
  isBlockEnabled: boolean
): { isLocked: boolean; lockedTooltip?: string } => {
  if (!isBlockEnabled) {
    return { isLocked: false };
  }

  const blockedSigner = getBlockedSignerContact(signers);
  if (!blockedSigner) {
    return { isLocked: false };
  }

  return {
    isLocked: true,
    lockedTooltip: getBlockedValidationDocSigningMessage(
      blockedSigner.validationStatus
    ),
  };
};

export {
  CONTACT_VALIDATION_STATUS,
  isBlockedValidationStatus,
  isDuplicatePhoneValidationStatus,
  isDuplicateEmailValidationStatus,
  getBlockedSignerContact,
  getBlockedValidationDocSigningMessage,
  isMissingContactValue,
  getMissingContactInfoMessage,
  getContactValidationDocSigningLock,
};
